import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { Observable } from 'rxjs';
import { UserState } from '../core/store/user/user.state';
import { PatientState } from '../core/store/patient/patient.state';
import { SetPreferredCodeMethod, SetPreferredLogin } from '../core/store/patient/patient.actions';

export type SignInState =
  | 'LOGIN'
  | 'RESET_PASSWORD'
  | 'RESET_SUCCESS'
  | 'UPDATE_PASSWORD'
  | 'ACTIVATE'
  | 'SEND_CODE'
  | 'VERIFY_CODE'
  | 'ERROR_ACCOUNT_ACTIVATED'
  | 'ERROR_TOKEN_EXPIRED';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent implements OnInit {
  @Select(UserState.isSessionExpired) isSessionExpired$!: Observable<boolean>;
  @Select(PatientState.preferredLogin) preferredLogin$!: Observable<'LOGIN' | 'SEND_CODE'>;
  @Select(PatientState.preferredCodeMethod) preferredCodeMethod$!: Observable<'email' | 'phone'>;

  signInState: SignInState = 'SEND_CODE';

  userToken: string | null;
  welcomeToken: string | null;
  resetPasswordToken: string | null;
  username: string | undefined;
  phoneNumber: string | undefined;
  factor: 'email' | 'phone' = 'email';

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private route: ActivatedRoute, private store: Store) {
    this.welcomeToken = this.route.snapshot.queryParamMap.get('welcome-token');
    this.resetPasswordToken = this.route.snapshot.queryParamMap.get('reset-password-token');
    this.username = this.route.snapshot.queryParamMap.get('username') ?? undefined;
    this.userToken = this.route.snapshot.queryParamMap.get('user-token') ?? 'none';
  }

  ngOnInit() {
    // When navigating to a protected route, the route path will be saved as the `originalUri`
    // If no `originalUri` has been saved, then redirect back to the app root
    const originalUri = this.oktaAuth.getOriginalUri();
    if (!originalUri || originalUri === window.location.origin) {
      this.oktaAuth.setOriginalUri('/');
    }

    if (this.resetPasswordToken) {
      this.signInState = 'UPDATE_PASSWORD';
    } else if (this.welcomeToken) {
      this.signInState = 'ACTIVATE';
    } else {
      this.preferredLogin$.subscribe((preferredLogin) => {
        this.signInState = preferredLogin;
      });

      this.preferredCodeMethod$.subscribe((preferredCodeMethod) => {
        this.factor = preferredCodeMethod;
      });
    }
  }

  onForgotPassword() {
    this.signInState = 'RESET_PASSWORD';
  }

  onSignInWithCode() {
    this.signInState = 'SEND_CODE';
    this.store.dispatch(new SetPreferredLogin('SEND_CODE'));
  }

  onSignIn() {
    this.signInState = 'LOGIN';
    this.store.dispatch(new SetPreferredLogin('LOGIN'));
  }

  onGoToSent() {
    this.signInState = 'RESET_SUCCESS';
  }

  onOtpSent(factor: 'email' | 'phone') {
    this.signInState = 'VERIFY_CODE';
    this.factor = factor;
    this.store.dispatch(new SetPreferredCodeMethod(factor));
  }

  setUsername(username: string) {
    this.username = username;
  }

  setPhoneNumber(phoneNumber: string) {
    this.phoneNumber = phoneNumber;
  }

  directOnError(errorCode: SignInState) {
    this.signInState = errorCode;
  }
}
